// ═══════════════════════════════════════════════════════════════════
// LOCK SCREEN
//
// The passphrase is the whole access-control system. It is derived to a
// key locally and never sent anywhere. Two consequences the screen has
// to communicate honestly:
//
//   1. A lost passphrase means unrecoverable data.
//   2. A MISTYPED passphrase silently addresses a different, empty vault
//      rather than failing. So when no data is found we stop and ask,
//      instead of quietly starting a second vault.
// ═══════════════════════════════════════════════════════════════════

import { useState } from 'react';
import { C } from './lib/format.js';
import { Btn, Notice } from './ui.jsx';
import { vaultFingerprint } from './lib/crypto.js';

export default function Lock({ onUnlock, pending, error, newVault, onConfirmNew, onCancelNew }) {
  const [pass, setPass] = useState('');
  const [confirm, setConfirm] = useState('');
  const [remember, setRemember] = useState(false);
  const [show, setShow] = useState(false);

  // ── Second stage: nothing found under this passphrase ──
  if (newVault) {
    return (
      <Shell>
        <h1 style={h1Style}>No data found</h1>
        <Notice tone="warn">
          There is nothing stored under this passphrase yet. That means one of two things:
        </Notice>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 18 }}>
          <div style={boxStyle}>
            <div style={{ color: C.bright, fontWeight: 600, fontSize: 14, marginBottom: 4 }}>This is your first time</div>
            <div style={{ fontSize: 13, color: C.dim, lineHeight: 1.6 }}>
              Perfectly normal — go ahead and create the vault.
            </div>
          </div>
          <div style={boxStyle}>
            <div style={{ color: C.amb, fontWeight: 600, fontSize: 14, marginBottom: 4 }}>Or you mistyped it</div>
            <div style={{ fontSize: 13, color: C.dim, lineHeight: 1.6 }}>
              If you have used this app before, a typo will look exactly like this — an empty vault rather than an error.
              Go back and re-enter the passphrase carefully.
            </div>
          </div>
        </div>

        <div style={{ margin: '20px 0', textAlign: 'center' }}>
          <div style={{ fontSize: 10, color: C.lbl, textTransform: 'uppercase', letterSpacing: 1.2 }}>Vault fingerprint</div>
          <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 22, color: C.gold, letterSpacing: 2, marginTop: 4 }}>
            {vaultFingerprint(newVault.vaultId)}
          </div>
          <div style={{ fontSize: 11, color: C.dim, marginTop: 6, lineHeight: 1.5 }}>
            Both of your devices must show this same code to be looking at the same vault.
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <Btn tone="primary" onClick={onConfirmNew}>Create a new vault with this passphrase</Btn>
          <Btn onClick={onCancelNew}>← Go back and re-enter</Btn>
        </div>
      </Shell>
    );
  }

  // ── First stage: enter the passphrase ──
  const tooShort = pass.length > 0 && pass.length < 8;
  const mismatch = confirm.length > 0 && confirm !== pass;
  const canSubmit = pass.length >= 8 && !pending;

  const submit = e => {
    e?.preventDefault();
    if (canSubmit) onUnlock(pass, remember);
  };

  return (
    <Shell>
      <h1 style={h1Style}>James &amp; Joanne</h1>
      <p style={{ fontSize: 13, color: C.dim, lineHeight: 1.7, marginBottom: 22 }}>
        Enter your shared passphrase to unlock. Everything is encrypted on this device before it is stored, so the
        passphrase never leaves your phone and the server only ever holds unreadable data.
      </p>

      <form onSubmit={submit}>
        <div style={{ fontSize: 12, color: C.lbl, marginBottom: 6 }}>Passphrase</div>
        <input
          type={show ? 'text' : 'password'}
          value={pass}
          onChange={e => setPass(e.target.value)}
          autoComplete="current-password"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          placeholder="Your shared passphrase"
          disabled={pending}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10, minHeight: 44 }}>
          <input type="checkbox" id="showpass" checked={show} onChange={e => setShow(e.target.checked)} />
          <label htmlFor="showpass" style={{ fontSize: 13, color: C.dim, cursor: 'pointer' }}>Show passphrase</label>
        </div>

        {tooShort && (
          <div style={{ fontSize: 12, color: C.amb, marginTop: 8 }}>
            Use at least 8 characters. A short phrase of a few unrelated words is both stronger and easier to type on a phone.
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6, minHeight: 44 }}>
          <input type="checkbox" id="remember" checked={remember} onChange={e => setRemember(e.target.checked)} />
          <label htmlFor="remember" style={{ fontSize: 13, color: C.dim, cursor: 'pointer', lineHeight: 1.5 }}>
            Stay unlocked on this device
          </label>
        </div>
        {remember && (
          <div style={{ fontSize: 11, color: C.amb, marginTop: -2, marginBottom: 8, lineHeight: 1.6 }}>
            Convenient, but it means anyone who can get past your device's own lock screen can open this app. Best left off
            on a shared or unlocked device.
          </div>
        )}

        {error && (
          <div style={{
            background: C.red + '18', border: `1px solid ${C.red}55`, borderRadius: 10,
            padding: '12px 14px', fontSize: 13, color: C.text, lineHeight: 1.6, margin: '14px 0',
          }}>{error}</div>
        )}

        <div style={{ marginTop: 18 }}>
          <Btn tone="primary" disabled={!canSubmit} onClick={submit} style={{ width: '100%' }}>
            {pending ? 'Unlocking…' : 'Unlock'}
          </Btn>
        </div>
        {pending && (
          <div style={{ fontSize: 11, color: C.dim, marginTop: 10, textAlign: 'center', lineHeight: 1.6 }}>
            Deriving your key — this deliberately takes a second or two, which is what makes the passphrase hard to
            brute-force.
          </div>
        )}
      </form>

      <div style={{ marginTop: 26 }}>
        <Notice tone="danger">
          <strong style={{ color: C.bright }}>If you lose this passphrase, the data is gone.</strong> There is no reset
          link and no account recovery — that is the trade-off for nobody else being able to read your finances. Use the
          <strong style={{ color: C.bright }}> Export everything</strong> button regularly and keep the file somewhere safe.
        </Notice>
      </div>
    </Shell>
  );
}

const h1Style = {
  fontSize: 26, fontWeight: 700, color: C.bright, letterSpacing: '-.5px', marginBottom: 8,
};

const boxStyle = {
  background: C.surf, border: `1px solid ${C.bdr}`, borderRadius: 10, padding: '14px 16px',
};

function Shell({ children }) {
  return (
    <div style={{
      minHeight: '100vh', background: C.bg, color: C.text,
      fontFamily: "'DM Sans',system-ui,sans-serif",
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px 18px',
    }}>
      <div style={{
        width: '100%', maxWidth: 460, background: C.card, border: `1px solid ${C.bdr}`,
        borderRadius: 16, padding: '28px 24px',
      }}>{children}</div>
    </div>
  );
}
